import type { DateInput } from "@/lib/utils/format";

/**
 * Normalise a `DateInput` to epoch ms, or null when it cannot be parsed.
 *
 * Both predicates below accept `DateInput`, not `Date`, because a `Date` is not
 * what they actually receive on every path. Feed rows reach the client two
 * different ways: an RSC/flight payload (which preserves real `Date` instances)
 * and `fetch("/api/feed") + res.json()` (which cannot — JSON has no date type,
 * so `Response.json()` serialises every `Date` to an ISO string and nothing
 * rehydrates it). The declared type said `Date` on both, so `.getTime()`
 * type-checked and then threw `TypeError: e.getTime is not a function` in
 * production the moment the feed was soft-navigated.
 *
 * `src/lib/utils/format.ts` and `hoursSince` in `src/lib/analytics/events.ts`
 * already settled this convention for `postedAt` — which is precisely why
 * `postedAt` never crashed and `expiresAt`, whose only client consumer is
 * `getDaysUntilExpiry`, did. This closes that gap at the consumer; the feed's
 * client fetch also rehydrates at the boundary now, so the type stops lying.
 */
function toMs(value: DateInput): number | null {
	const ms =
		value instanceof Date ? value.getTime() : new Date(value).getTime();
	return Number.isNaN(ms) ? null : ms;
}

/**
 * "Closes in Xd" urgency chip — days-remaining calculation for a job card.
 *
 * `jobs.expires_at` used to be dead: read by the render path (job detail's
 * indexability gate, JobPosting `validThrough`) but never written at ingest,
 * so every row was NULL and no "closes in Xd" UI could ever render. Ingest
 * now writes it (see `resolveExpiresAt` in `src/lib/ingestion/cron-shell-core.ts`)
 * — either an explicit source signal or a computed default window. This module
 * is the pure, testable piece that turns that timestamp into a chip.
 *
 * Deliberately conservative: a missing or already-past `expires_at` renders
 * NOTHING, never a negative/zero-day chip. `daysLeft` rounds UP (`Math.ceil`)
 * so a listing closing in 30 minutes still reads "closes in 1d", not "0d".
 */
export function getDaysUntilExpiry(
	expiresAt: DateInput | null | undefined,
	now: Date = new Date(),
): number | null {
	if (!expiresAt) return null;
	const ms = toMs(expiresAt);
	if (ms === null) return null;
	const msLeft = ms - now.getTime();
	if (msLeft <= 0) return null;
	return Math.ceil(msLeft / 86_400_000);
}

/**
 * True only when the SOURCE told us the posting has closed and that date has
 * passed — the single predicate allowed to take a live listing off the site
 * (404 on the detail page, noindex in `isJobIndexable`).
 *
 * `expiresAtSource` is `jobs.expires_at_source`: `"source"` (the board stated a
 * closing date), `"inferred"` (computed `posted_at + DEFAULT_JOB_EXPIRY_DAYS`
 * because it stated none), or null on rows written before the column existed.
 *
 * Only `"source"` counts. An inferred expiry falling into the past is an
 * artifact of the default window (45d) being SHORTER than the age sweep's
 * retirement window (60/90d) — every live listing crosses it. Treating that as
 * "gone" 404'd 2,854 of 5,949 live job pages on 2026-07-27. A legacy null fails
 * OPEN for the same reason: it cannot be proven to carry a real signal.
 *
 * Deliberately NOT merged into `getDaysUntilExpiry` — the chip is a
 * presentational hint that correctly renders nothing for any past date, whereas
 * this decides whether a page exists at all. Different blast radius, so they
 * stay separate predicates.
 */
export function isSourceStatedExpired(
	expiresAt: DateInput | null | undefined,
	expiresAtSource: string | null | undefined,
	now: Date = new Date(),
): boolean {
	if (expiresAtSource !== "source") return false;
	if (!expiresAt) return false;
	const ms = toMs(expiresAt);
	// An unparseable value cannot PROVE the posting closed, and this predicate
	// is the one that 404s a live page — so it fails OPEN, exactly as a legacy
	// null does above.
	if (ms === null) return false;
	return ms < now.getTime();
}
